// src/components/BoardPack/pages/consolidate_boardpack/Bridge_Summary_Page.jsx
import React, { useEffect, useRef, useState } from "react";
import { useQueue } from "@/context/QueueContext";
import { useNavigate } from "react-router-dom";
import ErrorState from "@/components/common/ErrorStateComponent";
import LoadingComponent from "@/components/common/LoadingComponent";
import { apiCall } from "@/api/apiCall";
import { processParams } from "../../utils/paramsHelper";
import { dynamicKey } from "../../utils/dynamicKey";
import Bridge_Summary_Chart_component from "../../components/consolidate_boardpack_components/Bridge_Summary_Chart_component";

const normalizeSummaryBlock = (block) => {
  if (!block) return [];

  return [
    ...(block.steps || []).map((s) => ({
      label: s.label,
      value: s.raw,
      type: s.type,
    })),
    { label: block.endLabel, value: block.endValueRaw, type: "total" },
  ];
};

// Fallback compare text agar koi custom template na mile (purana behaviour)
const compareTextForKey = (key, meta, page) => {
  if (page?.compareLabels?.[key]) return page.compareLabels[key];
  if (key === "vs_prior_year") return `${meta?.prevYear ?? ""}`.trim();
  if (key === "vs_budget") return "BUD";
  if (key === "vs_forecast") return "FCST";
  return key.replace(/^vs_/, "").replace(/_/g, " ").toUpperCase();
};

// Generic {{placeholder}} replacer
const applyTemplate = (template, vars) =>
  template.replace(/{{\s*(\w+)\s*}}/g, (_, k) => (vars[k] ?? "").toString());

// Purana generic default — sirf tab use hota hai jab config mein
// panelTitleTemplates ke andar us (period, key) ke liye kuch na diya ho
const DEFAULT_PANEL_TITLE_TEMPLATE =
  "MAWGIF {{contract}} {{year}} ACTUAL V {{compare}} {{month}}{{period}} VAR";

// Panel ka mini-title:
// 1) Pehle page.panelTitleTemplates[period][key] check karo (most specific — har
//    comparison ka apna word-order ho sakta hai, jaisa Prior Year / Budget / Forecast
//    ke liye alag-alag pattern chahiye)
// 2) Warna page.panelTitleTemplate (generic single template, purana wala)
// 3) Warna DEFAULT_PANEL_TITLE_TEMPLATE
const buildPanelTitle = ({ key, period, meta, contract, year, month, page }) => {
  // Contract ka display naam — config ka contractLabel priority mein,
  // warna meta.contract (jo API se aata hai, aksar sirf code hota hai)
  const contractLabel = page?.contractLabel || meta?.contract || contract || "";
  const yearLabel = meta?.year ?? year;
  const prevYearLabel = meta?.prevYear ?? "";
  const monthLabel = meta?.month ?? month;
  const compareText = compareTextForKey(key, meta, page);
  const periodSuffix = period === "ytd" ? " YTD" : "";

  const specificTemplate = page?.panelTitleTemplates?.[period]?.[key];
  const template =
    specificTemplate || page?.panelTitleTemplate || DEFAULT_PANEL_TITLE_TEMPLATE;

  return applyTemplate(template, {
    contract: contractLabel,
    year: yearLabel,
    prevYear: prevYearLabel,
    month: monthLabel,
    compare: compareText,
    period: periodSuffix,
  });
};

const Bridge_Summary_Page = ({ year = "", month = "", contract = "", page = null, isRefresh = false }) => {
  const { enqueue } = useQueue();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [monthData, setMonthData] = useState({});
  const [ytdData, setYtdData] = useState({});
  const [meta, setMeta] = useState(null);
  const hasFetchedRef = useRef(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const apiParams = processParams(page?.params || {}, {
        year,
        month,
        contract,
        currency: "Reporting Currency",
        is_cfo: 0,
      });

      if (!page?.api) {
        throw new Error("Missing 'api' in page config — this chart's endpoint must be set explicitly.");
      }

      const response = await enqueue(() =>
        apiCall("GET", page.api, apiParams, navigate, false, false, isRefresh)
      );

      if (!response?.success) {
        throw new Error(response?.message || "API returned unsuccessful response");
      }

      setMonthData(response.data?.month || {});
      setYtdData(response.data?.ytd || {});
      setMeta(response.meta);
    } catch (err) {
      console.error("Bridge_Summary fetch error:", err);
      setError(err.message || "Failed to load data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!year || !month || !contract) return;
    if (hasFetchedRef.current) return;

    hasFetchedRef.current = true;
    fetchData();
  }, [year, month, contract]);

  if (loading) {
    return <LoadingComponent />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={fetchData} />;
  }

  const comparisonKeys = Object.keys(monthData);

  const monthPanels = comparisonKeys.map((key) => ({
    key,
    panelTitle: buildPanelTitle({ key, period: "month", meta, contract, year, month, page }),
    data: normalizeSummaryBlock(monthData[key]),
  }));

  const ytdPanels = comparisonKeys.map((key) => ({
    key,
    panelTitle: buildPanelTitle({ key, period: "ytd", meta, contract, year, month, page }),
    data: normalizeSummaryBlock(ytdData[key]),
  }));

  return (
    <Bridge_Summary_Chart_component
      title={dynamicKey({ text: page?.title, year, month, contract })}
      subtitle={dynamicKey({ text: page?.subtitle, year, month, contract })}
      showComments={page?.showComments}
      monthPanels={monthPanels}
      ytdPanels={ytdPanels}
            footerLabel={page?.footerLabel ?? "Profit and Loss"}   // ✅ NEW

    />
  );
};

export default Bridge_Summary_Page;